import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ActivityConfiguredQuestResponse } from '@models/activity-configured-quest/activity-configured-quest-response';

import { ApiResponse } from '@models/api-response';
import { ClassroomQuestReport } from '@models/classroom-quest-report';
import { KeyValuePair } from '@models/key-value-pair';
import { SchoolLicense } from '@models/license/school-license';
import { PagedResponse } from '@models/paged-response';
import { IActivity, IQuest, Quest, questFactory } from '@models/quest';
import { QuestGroup } from '@models/quest-group';
import {
  AbandonQuestRequest,
  CloseAllOpenQuestsRequest,
  CloseAllOpenQuestsResponse,
  CreateQuestRequest,
  ReassignQuestRequest,
  ReassignUpdateQuestRequest,
  UpdateQuestRequest
} from '@models/quest-request';
import { ReassignQuestResponse, ReassignUpdateQuestResponse, UpdateQuestResponse } from '@models/quest-response';
import { QuestScoreReport } from '@models/quest-score-report';
import questTypes from '@models/quest-type/quest-types';
import {
  AssessmentManualScoreUnitedResponse,
  DeleteScoresConfirmationResponse,
  IAssessmentObjectiveManualPart,
  ISuperkidsLegacyAssessmentPartManualScore,
  ISuperkidsLegacyAssessmentPartProgressDetail,
  ISuperkidsLegacyAssessmentScoreDetail
} from '@models/score';
import { StudentActivityReport } from '@models/student-activity-report';
import {
  QuestGroupSearchParams,
  QuestSearchParams,
  QuestUpdateReassignBody
} from '@services/quest/quest.service.model';
import { IQuestClosedScore } from '@shared/branded-quest-closed-view-scores/models/quest-closed-score.model';
import { GroupWithQuests, StudentWithQuests } from '@shared/branded-quest-planner/branded-quest-planner.model';
import { IQuestClosed } from '@shared/branded-quests-closed/models/quest-closed.model';
import { DateHelpers } from '@shared/date-helpers';
import { GradeType } from '@shared/enums/grade-type';
import { ProductType } from '@shared/enums/product-type';
import { QuestClosedViewByMode } from '@shared/enums/quest-closed-view-by-mode';
import { QuestAssignedByType, QuestStatus, QuestType } from '@shared/enums/quest-type';
import { ScoreBenchmark } from '@shared/enums/score-benchmark';
import { Helpers } from '@shared/helpers';
import { DeleteScoresConfirmationParams } from '@shared/modals/score-detail-modal/score-detail-params';
import {
  instantiateApiResponseFromArray,
  instantiateApiResponseFromJson,
  instantiatePagedResponseFromArrayInterface,
} from '@shared/zb-rxjs-operators/rxjs-operators';
import moment from 'moment';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { TopicService } from 'src/app/products/topic/topic.service';
import { AppConfigService } from '../../appconfig.service';

@Injectable({
  providedIn: 'root',
})
export class QuestService {
  constructor(
    private http: HttpClient,
    private appConfig: AppConfigService,
  ) {
  }

  /** Used by the Quest Closed Page to limit results in API request and for rendering pagination. */
  readonly QUESTS_CLOSED_PAGE_SIZE = 25;

  private getQuestUrl(action: string = ''): string {
    if (action === 'reassign') {
      return `${this.appConfig.apiUrl}/teacher/quest/reassign`;
    }

    const actionPart = action ? `/${action}` : '';
    return `${this.appConfig.apiUrl}/quest${actionPart}`;
  }

  private getClassroomQuestsUrl(classroomId: string, startDate: Date, filter: string): string {
    const startDateFormatted = DateHelpers.toISO8601(startDate);
    return `${this.appConfig.apiUrl}/quest/classroom/${classroomId}/week/${startDateFormatted}/${filter}`;
  }

  private getQuestReportUrl(
    action: string, baseEndpoint: string, classroomId: string, groupId?: string, schoolYear?: string,
    showPastYearReports?: boolean
  ): string {
    let baseUrl = `${this.appConfig.apiUrl}/${baseEndpoint}/classroom/${classroomId}/${action}`;
    if (groupId) {
      if (action === 'assigned-work') {
        baseUrl += `?groupId=${groupId}`;
      } else {
        baseUrl += `/group/${groupId}`;
      }
    }
    if (baseEndpoint === 'export') {
      const offset = moment().utcOffset();
      const separator = baseUrl.includes('?') ? '&' : '?';
      baseUrl += `${separator}offsetInMinutes=${offset}`;
    }

    baseUrl += this.addSchoolYear(baseUrl, schoolYear);
    baseUrl += this.addShowPastYearReports(baseUrl, showPastYearReports);

    return baseUrl;
  }

  // The assessment question report is under a different endpoint than other reports because.
  private getQuestAssessmentReportUrl(
    activityId: string, action: string = 'question-report',
    schoolYear: string = null, showPastYearReports: boolean = false
  ): string {
    let baseUrl = `${this.appConfig.apiUrl}/digital-resources/assessment/${activityId}/${action}`;

    baseUrl += this.addSchoolYear(baseUrl, schoolYear);
    baseUrl += this.addShowPastYearReports(baseUrl, showPastYearReports);

    return baseUrl;
  }

  private getManualScoreUrl(
    questKey: string, product: ProductType, grade: GradeType | string,
    classroomId: string, userId: string, objective: string, schoolYear?: string, showPastYearReports?: boolean
  ): string {
    let baseUrl = `${this.appConfig.apiUrl}/digital-resources/assessment/manual-score/${questKey}/product/${product}/grade/${grade}/classroom/${classroomId}/user/${userId}/objective/${objective}`;

    baseUrl += this.addSchoolYear(baseUrl, schoolYear);
    baseUrl += this.addShowPastYearReports(baseUrl, showPastYearReports);

    return baseUrl;
  }

  getAssessmentManualScore(params: {
    assessmentKey: string,
    productType: ProductType,
    gradeType :GradeType,
    classroomId: string,
    studentId: string,
  }): Observable<ApiResponse<AssessmentManualScoreUnitedResponse>> {
    const url = `${this.appConfig.apiUrl}/digital-resources/assessment/manual-score/${params.assessmentKey}/product/${params.productType}/grade/${params.gradeType}/classroom/${params.classroomId}/user/${params.studentId}`;
    return this.http.get(url).pipe(
      map((r: ApiResponse<AssessmentManualScoreUnitedResponse>) => new ApiResponse(true, r)),
    );
  }

  setAssessmentManualScore(params: {
    assessmentKey: string,
    productType: ProductType,
    gradeType: GradeType,
    classroomId: string,
    studentId: string,
    data: {part: string, totalCorrect: number}[],
  }): Observable<any> {
    const url = `${this.appConfig.apiUrl}/digital-resources/assessment/manual-score/${params.assessmentKey}/product/${params.productType}/grade/${params.gradeType}/classroom/${params.classroomId}/user/${params.studentId}`;
    return this.http.patch(url, params.data).pipe(
      map(r => new ApiResponse(true, r)),
    );
  }

  getDeleteScoresConfirmation(
    params: DeleteScoresConfirmationParams
  ): Observable<ApiResponse<DeleteScoresConfirmationResponse[]>> {
    const url = `${this.appConfig.apiUrl}/digital-resources/assessment/${params.assessmentKey}/history?IncludeLogicallyDeleted=${params.includeLogicallyDeleted}&UserId=${params.userId}&ProductType=${params.productType}&PageNumber=${params.pageNumber}&PageSize=${params.pageSize}`;
    return this.http.get(url).pipe(
      map((r: ApiResponse<DeleteScoresConfirmationResponse[]>) => new ApiResponse(true, r)),
    );
  }

  deleteCurrentScore(activityId: string): Observable<ApiResponse<AssessmentManualScoreUnitedResponse>> {
    const url = `${this.appConfig.apiUrl}/digital-resources/assessment/${activityId}`;

    return this.http.delete(url).pipe(
      map((r: ApiResponse<ApiResponse<AssessmentManualScoreUnitedResponse>>) => new ApiResponse(true, r)),
    )
  }

  private addSchoolYear(baseUrl: string, schoolYear?: string): string {
    const separator = baseUrl.includes('?') ? '&' : '?';
    return schoolYear ? `${separator}schoolYear=${schoolYear}` : '';
  }

  private addShowPastYearReports(baseUrl: string, showPastYearReports?: boolean): string {
    const separator = baseUrl.includes('?') ? '&' : '?';
    return showPastYearReports ? `${separator}showPastYearReports=${showPastYearReports}` : '';
  }

  createQuest(data: CreateQuestRequest): Observable<ApiResponse<Partial<IQuest[]>>> {
    const values: CreateQuestRequest = {
      ...data,
      startOn: this.normalizeToDateTime(data.startOn),
    };
    if (data.expiresOn) {
      values.expiresOn = this.normalizeToDateTime(data.expiresOn);
    }

    return this.http.post<ApiResponse<IQuest[]>>(this.getQuestUrl(), values)
      .pipe(
        map((r: ApiResponse<Partial<IQuest[]>>) => new ApiResponse<Partial<IQuest[]>>(true, r)),
      );
  }

  getActivityBasedQuestOptions(
    classroomId: string, grade: string, productType: ProductType
  ): Observable<ApiResponse<ActivityConfiguredQuestResponse>> {
    return this.http.get(`${this.appConfig.apiUrl}/digital-resources/navigation/create-quest/classroom/${classroomId}/?grade=${grade}&productType=${productType}`)
      .pipe(
        map(response => new ApiResponse<ActivityConfiguredQuestResponse>(true, response))
      );
  }

  /**
   * @deprecated use {@link searchQuests} instead, since it provides type safety.
   */
  getQuests(params: KeyValuePair[]): Observable<ApiResponse<Quest[]>> {
    return this.findQuests(params);
  }


  getQuestsByGroupId(id: string, isAggregate?: boolean): Observable<ApiResponse<Quest[]>> {
    const params = [
      { key: 'groupId', value: id },
      { key: 'activeOnly', value: false },
      { key: 'excludeAggregateQuests', value: !isAggregate },
    ];

    this.getAllProductTypes().forEach((p) => {
      params.push({ key: 'productTypes', value: p });
    });
    return this.findQuests(params);
  }

  getClassroomQuestsByGroup(classroomId: string, startDate: Date): Observable<ApiResponse<GroupWithQuests[]>> {
    const groupFilter = 'by-group';
    return this.http.get<ApiResponse<GroupWithQuests[]>>(this.getClassroomQuestsUrl(classroomId,
      startDate,
      groupFilter))
      .pipe(
        map(res => new ApiResponse<GroupWithQuests[]>(true, {
          response: res.response,
          messages: res.messages,
        })),
      );
  }

  getClassroomQuestsByStudent(classroomId: string, startDate: Date): Observable<ApiResponse<StudentWithQuests[]>> {
    const studentFilter = 'by-student';
    return this.http.get<ApiResponse<StudentWithQuests[]>>(this.getClassroomQuestsUrl(classroomId,
      startDate,
      studentFilter))
      .pipe(
        map(res => new ApiResponse<StudentWithQuests[]>(true, {
          response: res.response,
          messages: res.messages,
        })),
      );
  }

  getAssignedClassroomQuests(
    classroomId: string, statusType: QuestStatus = QuestStatus.Active
  ): Observable<ApiResponse<QuestGroup[]>> {
    const productTypes: string[] = this.getAllProductTypes();
    const params = new HttpParams({
      fromObject: {
        statusType,
        assignedByType: 'Teacher',
        classroomId,
        productTypes,
      },
    });

    return this.http.get<ApiResponse<QuestGroup[]>>(this.getQuestUrl('group'), { params })
      .pipe(
        map(res => new ApiResponse<QuestGroup[]>(true, res)),
      );
  }

  getClassroomClosedQuestsByView(
    classroomId: string, viewedBy: QuestClosedViewByMode, orderBy: string, pageNumber: number = 1
  ): Observable<PagedResponse<IQuestClosed[]>> {
    const url = `${this.appConfig.apiUrl}/quest/classroom/${classroomId}/quest-closed`;
    const params = {
      pageSize: this.QUESTS_CLOSED_PAGE_SIZE,
      pageNumber,
      orderBy,
    };

    if (viewedBy !== QuestClosedViewByMode.All) {
      params['viewedBy'] = viewedBy;
    }

    return this.http.get<PagedResponse<IQuestClosed[]>>(url, { params }).pipe(
      instantiatePagedResponseFromArrayInterface<IQuestClosed>()
    );
  }

  getClosedQuestScore(classroomId: string, groupId: string): Observable<ApiResponse<IQuestClosedScore[]>> {
    const url = `${this.appConfig.apiUrl}/quest/classroom/${classroomId}/group/${groupId}/quest-closed-detail`;
    const params = {
      classroomId,
      groupId,
    };

    return this.http.get<ApiResponse<IQuestClosedScore[]>>(url, { params })
      .pipe(
        map(res => new ApiResponse<IQuestClosedScore[]>(true,
          {
            response: res.response,
            messages: res.messages,
          })),
      );
  }

  updateQuest(request: ReassignUpdateQuestRequest): Observable<ApiResponse<ReassignUpdateQuestResponse>> {
    if (request.expiresOn) {
      request.expiresOn = this.normalizeToDateTime(request.expiresOn);
    }
    if (request.closedOn) {
      request.closedOn = this.normalizeToDateTime(request.closedOn);
    }

    return this.http.patch<ApiResponse<ReassignUpdateQuestResponse>>(this.getQuestUrl('update-reassign-request'),
      request)
      .pipe(
        map(res => new ApiResponse<ReassignUpdateQuestResponse>(true, res)),
      );
  }

  searchQuestGroups(searchParams: QuestGroupSearchParams): Observable<ApiResponse<QuestGroup[]>> {
    const params = this.getHttpParams({
      ...searchParams,
      // product types are required, default to search for all
      productTypes: searchParams.productTypes ?? this.getAllProductTypes(),
    });
    return this.http.get<ApiResponse<QuestGroup[]>>(this.getQuestUrl('group'), { params })
      .pipe(map(res => new ApiResponse(true, res)));
  }

  updateReassignQuest(data: QuestUpdateReassignBody): Observable<ApiResponse<any>> {
    return this.http.patch(
      this.getQuestUrl('update-reassign-request'), {
        ...data,
        expiresOn: data.expiresOn ? this.normalizeToDateTime(data.expiresOn) : undefined,
        closedOn: data.closedOn ? this.normalizeToDateTime(data.closedOn) : undefined,
      }).pipe(map(res => new ApiResponse<any>(true, res)),
    );
  }

  updateQuests(data: UpdateQuestRequest[]): Observable<ApiResponse<UpdateQuestResponse[]>> {
    const requests = data.map((request: UpdateQuestRequest) => {
      if (request.startOn) {
        request.startOn = this.normalizeToDateTime(request.startOn);
      }
      if (request.expiresOn) {
        request.expiresOn = this.normalizeToDateTime(request.expiresOn);
      }
      if (request.closedOn) {
        request.closedOn = this.normalizeToDateTime(request.closedOn);
      }
      return request;
    });

    return this.http.patch<ApiResponse<UpdateQuestResponse[]>>(this.getQuestUrl(), requests)
      .pipe(
        map(res => new ApiResponse<UpdateQuestResponse[]>(true, res)),
      );
  }

  deleteQuestGroup(
    groupId: string, grade: GradeType | string, productType: ProductType, classroomId: string
  ): Observable<ApiResponse<string[]>> {
    const data = {
      groupId,
      grade,
      productType,
      classroomId,
      assignedByType: QuestAssignedByType.Teacher,
      userIds: [],
    } as AbandonQuestRequest;
    return this.http.request('delete', this.getQuestUrl(), { body: data })
      .pipe(
        map(res => new ApiResponse<string[]>(true, res)),
      );
  }

  closeAllOpenQuests(
    userIdToClose: string, classroomGroupIdToClose: string, classroomId: string
  ): Observable<ApiResponse<CloseAllOpenQuestsResponse>> {
    const data = {
      userIdToClose,
      classroomGroupIdToClose,
      classroomId,
    } as CloseAllOpenQuestsRequest;

    return this.http.patch(this.getQuestUrl('close'), data)
      .pipe(
        map(res => new ApiResponse<CloseAllOpenQuestsResponse>(true, res)),
      );
  }

  removeStudentFromQuestGroup(
    groupId: string, questType: QuestType, productType: ProductType, usersToRemove: string[]
  ): Observable<ApiResponse<ReassignQuestResponse>> {
    const data = {
      groupId,
      questType,
      productType,
      userIdsToRemove: usersToRemove,
      userIdsToAdd: [],
    } as ReassignQuestRequest;
    return this.http.patch(this.getQuestUrl('reassign'), data)
      .pipe(
        map(res => new ApiResponse<ReassignQuestResponse>(true, res)),
      );
  }

  addStudentsToQuestGroup(groupId: string,
    questType: QuestType,
    productType: ProductType,
    numberOfStarsToEarn: number,
    usersToAdd: string[]): Observable<ApiResponse<ReassignQuestResponse>> {
    const data = {
      groupId,
      questType,
      productType,
      numberOfStarsToEarn,
      userIdsToAdd: usersToAdd,
      userIdsToRemove: [],
    } as ReassignQuestRequest;
    return this.http.patch(this.getQuestUrl('reassign'), data)
      .pipe(
        map(res => new ApiResponse<ReassignQuestResponse>(true, res)),
      );
  }

  getClassroomQuestReport(
    classroomId: string, groupId?: string, schoolYear?: string, showPastYearReports?: boolean,
    classroomLicenses?: SchoolLicense[]
  ): Observable<ApiResponse<ClassroomQuestReport[]>> {
    if (!classroomId) {
      return this.getNoClassroomError();
    }

    const url = this.getQuestReportUrl(
      'assigned-work',
      'report',
      classroomId,
      groupId,
      schoolYear,
      showPastYearReports);
    return this.http.get(url)
      .pipe(
        map((res: any) => {
          if (res.response && res.response.length > 0) {
            if (classroomLicenses) {
              res.response = res.response
                .filter(questReport => (classroomLicenses
                  .find(l => l.productType === questReport.productType
                    && Helpers.mapVariantToGradeType(l.variant) === Helpers.mapVariantToGradeType(questReport.grade))));
            }
          }
          return res;
        }),
        instantiateApiResponseFromArray(ClassroomQuestReport),
      );
  }

  static isAggregate(quest: Quest): boolean {
    return quest.questType === QuestType.AggregateQuest;
  }

  static isCustomActivity(license: SchoolLicense, activity: IActivity): boolean {
    const quest = (TopicService.getQuest(activity.quests)) as any;
    return QuestService.isHandwriting(license.productType)
      && (quest.topicType && (quest.topicType === 'CustomFreeWrite'));
  }

  /**
   * TODO should be removed/merged with similar method {@link ProductService#isSpelling}.
   */
  static isSpelling(productType: ProductType): boolean {
    return (productType === ProductType.spcn2022
      || productType === ProductType.spcn2020tx
      || productType === ProductType.spcn2020txs
      || productType === ProductType.spcn2016n);
  }

  /**
   * TODO should be removed/merged with similar method {@link ProductService#isHandwriting}.
   */
  static isHandwriting(productType: ProductType): boolean {
    return (productType === ProductType.hw2020n
      || productType === ProductType.hw2025n
      || productType === ProductType.hw2020tx
      || productType === ProductType.hw2020txs
      || productType === ProductType.laesc2020n
      || productType === ProductType.laesc2020tx
      || productType === ProductType.laesc2020txs);
  }

  /**
   * TODO should be removed/merged with similar method {@link ProductService#isSuperkids}.
   */
  static isSuperkids(productType: ProductType): boolean {
    return (productType === ProductType.sk2015
      || productType === ProductType.sk2017);
  }

  searchQuests(searchParams: QuestSearchParams): Observable<ApiResponse<Quest[]>> {
    const params = this.getHttpParams({
      ...searchParams,
      // product types are required, default to search for all
      productTypes: searchParams.productTypes ?? this.getAllProductTypes(),
    });
    return this.http.get<ApiResponse<Quest[]>>(this.getQuestUrl(''), { params })
      .pipe(map(res => new ApiResponse(true, res)));
  }

  private findQuests(params: KeyValuePair[]): Observable<ApiResponse<Quest[]>> {
    const url = Helpers.buildUrlWithParameters(this.appConfig.apiUrl, '/quest', params);
    return this.http.get<ApiResponse<Quest[]>>(url)
      .pipe(
        map((r) => {
          const quests = r.response.map(q => questFactory(questTypes, q.questType, q));
          return new ApiResponse<Quest[]>(true, { response: quests, messages: [] });
        }),
      );
  }

  getLatestStudentActivityReport(classroomId: string,
    userId?: string,
    schoolYear?: string,
    showPastYearReports?: boolean,
    classroomLicenses?: SchoolLicense[],
    isParent?: boolean): Observable<ApiResponse<StudentActivityReport[]>> {
    if (!classroomId) {
      return this.getNoClassroomError();
    }
    const action = userId ? `independent-work/users/${userId}` : 'independent-work';
    return this.http.get(this.getQuestReportUrl(action, 'report', classroomId, null, schoolYear, showPastYearReports))
      .pipe(
        map((res: any) => {
          if (res.response && res.response.length > 0) {
            if (classroomLicenses && isParent) {  // only filter when a parent, teachers & admins should see all work
              res.response = res.response
                .filter(questReport => (classroomLicenses
                  .find(l => l.productType === questReport.productType
                    && Helpers.mapVariantToGradeType(l.variant) === Helpers.mapVariantToGradeType(questReport.grade))));
            }
            res.response = res.response
              .map((report) => {
                if (!report.grade && report.topicKey) {
                  const gradeMatches = report.topicKey.match(/G([K0-9]+)/);
                  if (gradeMatches && gradeMatches.length > 1) {
                    return {
                      ...report,
                      grade: Helpers.mapGradeToGradeType(gradeMatches[1]),
                    };
                  }
                }
                return report;
              });
          }
          return res;
        }),
        instantiateApiResponseFromArray(StudentActivityReport),
      );
  }

  getReportExport(
    type: string, classroomId: string, userOrGroupId?: string, schoolYear?: string, showPastYearReports?: boolean
  ): Observable<Blob> {
    if (!classroomId) {
      return of(null);
    }

    const action = userOrGroupId && type === 'independent-work' ? `${type}/users/${userOrGroupId}` : type;
    const groupId = type !== 'independent-work' ? userOrGroupId : null;
    return this.http.get(
      this.getQuestReportUrl(action, 'export', classroomId, groupId, schoolYear, showPastYearReports), {
        responseType: 'blob'
      });
  }

  getQuestScoreReport(
    classroomId: string, questKey: string, groupId: string, schoolYear?: string, showPastYearReports?: boolean
  ): Observable<ApiResponse<QuestScoreReport>> {
    if (!classroomId) {
      return this.getNoClassroomError();
    }
    const action = `assigned-work/${questKey}/group/${groupId}`;
    return this.http.get(this.getQuestReportUrl(action, 'report', classroomId, null, schoolYear, showPastYearReports))
      .pipe(
        instantiateApiResponseFromJson(QuestScoreReport),
      );
  }

  getQuestAssessmentReport(
    activityId: string, schoolYear?: string, showPastYearReports?: boolean
  ): Observable<ApiResponse<ISuperkidsLegacyAssessmentScoreDetail>> {
    const url = this.getQuestAssessmentReportUrl(activityId, 'question-report', schoolYear, showPastYearReports);
    return this.http.get(url)
      .pipe(
        instantiateApiResponseFromJson(ISuperkidsLegacyAssessmentScoreDetail),
      );
  }

  getQuestAssessmentExport(activityId: string, schoolYear?: string, showPastYearReports?: boolean): Observable<Blob> {
    const url = this.getQuestAssessmentReportUrl(
      activityId,
      'question-report-export',
      schoolYear,
      showPastYearReports);
    return this.http.get(url, { responseType: 'blob' });
  }

  getManualScoreForAssessment(
    questKey: string, product: ProductType, grade: GradeType | string,
    classroomId: string, userId: string, objective: string, schoolYear?: string,
    showPastYearReports?: boolean
  ): Observable<ApiResponse<ISuperkidsLegacyAssessmentPartProgressDetail[]>> {
    const url: string = this.getManualScoreUrl(
      questKey,
      product,
      grade,
      classroomId,
      userId,
      objective,
      schoolYear,
      showPastYearReports);
    return this.http.get<ApiResponse<ISuperkidsLegacyAssessmentPartManualScore>>(url)
      .pipe(
        map(res => new ApiResponse<ISuperkidsLegacyAssessmentPartProgressDetail[]>(true, {
          response: res.response.parts.map(p => ({
            ...p,
            totalCorrect: p.benchmark === ScoreBenchmark.NotScored ? '' : p.totalCorrect,
            // Map missing Api properties from the parameters.
            assessmentKey: questKey,
            isInProgress: false,
          })),
          messages: res.messages,
        })),
      );
  }

  setManualScoreForAssessment(
    questKey: string, product: ProductType, grade: GradeType | string, classroomId: string, userId: string,
    objective: string, parts: IAssessmentObjectiveManualPart[]
  ): Observable<ApiResponse<ISuperkidsLegacyAssessmentPartManualScore>> {
    return this.http.patch(this.getManualScoreUrl(questKey, product, grade, classroomId, userId, objective), parts)
      .pipe(
        map(res => new ApiResponse<ISuperkidsLegacyAssessmentPartManualScore>(true, res)),
      );
  }

  /**
   * TODO this should be moved to the {@link ProductService}.
   */
  getAllProductTypes(): string[] {
    return Object['values'](ProductType).reduce((result: string[], key: string) => {
      if (!result.includes(key)) {
        result.push(key);
      }
      return result;
    }, []);
  }

  private getHttpParams(parameters: Record<string, any>) {
    const normalized: Record<string, any> = {};
    Object.keys(parameters).forEach((key: string) => {
      const value = parameters[key];
      if (value instanceof Date) {
        normalized[key] = this.normalizeToDateTime(value);
        console.log('DATE key => value', key, normalized[key]);
      } else {
        normalized[key] = value;
      }
    });
    return new HttpParams({
      fromObject: normalized
    });
  }

  /**
   * Normalizes an ISO date to DateTime ISO string.
   *
   * @param {string | Date} isoDate
   *   A date string to pass to moment.
   *
   * @returns {string}
   *    2020-03-06T12:43:28.872Z
   */
  private normalizeToDateTime(isoDate: string | Date): string {
    return moment(isoDate).toISOString();
  }

  private getNoClassroomError(): Observable<ApiResponse<any>> {
    console.error('ClassroomId is not defined');
    return of(new ApiResponse<any>(false, {
      response: [],
      messages: [`An error occurred due to an issue with this classroom data. Logging out and back in again may resolve the issue.`],
    }));
  }
}
